import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import Layout from '../shared/Layout';
import PageHeader from '../shared/PageHeader';
import FilterBar from '../../components/settings/FilterBar';
import Icon from '../../components/tables/Icon';
import IconAdd from '../../components/icons/IconAdd';
import IconGroups from '../../components/icons/IconGroups';
import IconClipboardList from '../../components/icons/IconClipboardList';
import IconCalendarDate from '../../components/icons/IconCalendarDate';
import GroupModal from './GroupModal';
import language from '../../variables/language';
import '../../css/groups.css';

const LUXON_LOCALE_BY_LANG = { en: 'en', ua: 'uk', pl: 'pl' };

const Groups = () => {
  const navigate = useNavigate();
  const lang = language[language.lang];
  const locale = LUXON_LOCALE_BY_LANG[language.lang] || 'en';

  const [groups, setGroups] = useState([]);
  const [studentCounts, setStudentCounts] = useState({});
  // per-group enrichment (courses + next lesson) - only 6-ish groups exist
  // in practice, so one small request pair per group on list load is
  // negligible; there's no bulk "all groups' courses/next lesson" endpoint
  // to reach for instead.
  const [enrichment, setEnrichment] = useState({});
  const [search, setSearch] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [modalState, setModalState] = useState('none');
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    axios.get('/api/groups', { withCredentials: true })
      .then(response => {
        const list = response.data || [];
        setGroups(list);
        const todayStr = DateTime.now().toFormat('yyyy-MM-dd');
        Promise.all(list.map(group =>
          Promise.all([
            axios.get('/api/groups/' + group.id + '/courses', { withCredentials: true }).catch(() => ({ data: [] })),
            axios.get('/api/lessons?groupe=' + group.id, { withCredentials: true }).catch(() => ({ data: [] })),
          ]).then(([coursesRes, lessonsRes]) => {
            const lessons = Array.isArray(lessonsRes.data) ? lessonsRes.data : [];
            // some legacy rows carry a literal "No date" placeholder rather
            // than a real ISO date (or no date at all) - a plain string
            // compare against todayStr would sort "No date" as if it were
            // some far-future date (capital N > any digit), so require it
            // to actually parse as a real calendar date first.
            const upcoming = lessons
              .filter(l => DateTime.fromFormat(l.date || '', 'yyyy-MM-dd').isValid && l.date >= todayStr)
              .sort((a, b) => (a.date + a.start).localeCompare(b.date + b.start));
            return {
              id: group.id,
              courses: Array.isArray(coursesRes.data) ? coursesRes.data : [],
              lessonsCount: lessons.length,
              nextLesson: upcoming[0] || null,
            };
          })
        )).then(results => {
          const byId = {};
          results.forEach(r => { byId[r.id] = r; });
          setEnrichment(byId);
        });
      })
      .catch(err => {
        if (err.response && err.response.status === 401) navigate('/login');
      });
    axios.get('/api/students', { withCredentials: true })
      .then(response => {
        const counts = {};
        (response.data || []).forEach(student => {
          if (student.groupe && student.groupe.id) {
            counts[student.groupe.id] = (counts[student.groupe.id] || 0) + 1;
          }
        });
        setStudentCounts(counts);
      })
      .catch(() => {});
  }, [refresh, navigate]);

  const visibleGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = query
      ? groups.filter(group => (group.name || '').toLowerCase().includes(query))
      : groups;
    return [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [groups, search]);

  const refreshList = () => setRefresh(prev => prev + 1);

  const openCreate = () => { setEditId(null); setModalState('create'); };
  const stateHundler = (state, id) => {
    if (state === 'edit') {
      setEditId(id);
      setModalState('edit');
    }
  };
  const closeModal = () => setModalState('none');
  const onSaved = () => { closeModal(); refreshList(); };

  const formatNextLesson = (lesson) => {
    if (!lesson) return null;
    const dt = DateTime.fromFormat(lesson.date, 'yyyy-MM-dd').setLocale(locale);
    if (!dt.isValid) return null;
    const todayStr = DateTime.now().toFormat('yyyy-MM-dd');
    const dayLabel = lesson.date === todayStr ? lang.today : dt.toFormat('dd.MM');
    return dayLabel + ' · ' + lesson.start;
  };

  return (
    <Layout>
      <PageHeader name={lang['groups']}>
        <FilterBar
          search={{ value: search, onChange: setSearch, placeholder: lang.placeholderSearch }}
          resultCount={visibleGroups.length}
        />
        <div className="groups-header-actions">
          <div className="groups-add-btn" onClick={openCreate} title={lang.groupCard.addStudentButton}>
            <IconAdd />
          </div>
        </div>
      </PageHeader>
      <main className="main-block">
        <div className="main-content txt-c groups-page">
          {visibleGroups.length === 0 ? (
            <p className="group-attendance-empty">{lang['Empty list']}</p>
          ) : (
            <div className="groups-list">
              {visibleGroups.map((group, index) => {
                const info = enrichment[group.id];
                const nextLessonText = info ? formatNextLesson(info.nextLesson) : null;
                return (
                  <div className="groups-row" key={group.id}
                    style={{ animationDelay: Math.min(index * 40, 320) + 'ms' }}
                    onClick={() => navigate('/groups/' + group.id)}>
                    <span className="groups-row-avatar">{(group.name || '?').charAt(0).toUpperCase()}</span>

                    <span className="groups-row-name">{group.name}</span>

                    <span className="groups-row-stat groups-row-members" title={lang.groupsPage.membersColumn}>
                      <IconGroups />{studentCounts[group.id] || 0}
                    </span>

                    <span className="groups-row-courses">
                      {!info ? (
                        <span className="groups-row-skeleton" />
                      ) : info.courses.length === 0 ? (
                        <span className="groups-row-courses-empty">{lang.noCourses}</span>
                      ) : (
                        info.courses.slice(0, 3).map(course => (
                          <span className="groups-course-chip" key={course.id}>
                            <IconClipboardList />{course.name}
                          </span>
                        ))
                      )}
                      {info && info.courses.length > 3 ? (
                        <span className="groups-course-chip groups-course-chip-more">+{info.courses.length - 3}</span>
                      ) : ''}
                    </span>

                    <span className={'groups-row-next-lesson' + (info && info.nextLesson ? '' : ' empty')}>
                      <IconCalendarDate />
                      {!info ? <span className="groups-row-skeleton" /> : (nextLessonText || lang.groupsPage.noNextLesson)}
                    </span>

                    <span className="groups-row-actions" onClick={e => e.stopPropagation()}>
                      <Icon mode="edit" id={group.id} stateHundler={stateHundler}
                        className="icon-edit" classes="icon-dimensions-big" />
                      <Icon mode="delete" url={'/api/groups/' + group.id} onRefreshHundler={refreshList}
                        className="icon-delete" classes="icon-dimensions-big" />
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <GroupModal modalState={modalState} groupId={editId} onClose={closeModal} onSaved={onSaved} />
    </Layout>
  );
};

export default Groups;
